import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { formatTaxIdentifier, normalizeEmail, validateCustomerIdentity, type CustomerKind, type ValidationCountry } from "./validation";
import {
  OpportunityDialog,
  type OpportunityRecord,
  type OpportunityTask,
  type PublicVehicleIntake,
  type PublicFinancingRequest,
} from "./OpportunityDialog";
import "./stand-crm.css";

type Contact = {
  id: string;
  branch_id: string;
  public_reference: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  channel: string;
  source_label: string | null;
  origin_vehicle_id: string | null;
  assigned_to: string;
  status: string;
  notes: string | null;
  first_contact_at: string;
  last_interaction_at: string;
  converted_opportunity_id: string | null;
  closed_reason: string | null;
  merged_into_contact_id: string | null;
  created_at: string;
};
type ContactMerge = {
  id: string;
  primary_contact_id: string;
  duplicate_contact_id: string;
  reason: string;
  merged_at: string;
  reversed_at: string | null;
  reversal_reason: string | null;
};
type Interaction = {
  id: string;
  contact_id: string;
  channel: string;
  direction: string;
  interaction_type: string;
  summary: string;
  occurred_at: string;
  created_by: string;
};
type ContactEvent = {
  id: number;
  contact_id: string;
  event_type: string;
  message: string | null;
  created_at: string;
};
type Conversation = {
  id: string;
  contact_id: string;
  channel: string;
  status: string;
  assigned_to: string;
  updated_at: string;
};
type ConversationMessage = {
  id: number;
  conversation_id: string;
  direction: string;
  body: string;
  created_at: string;
};
type Customer = {
  id: string;
  customer_type: CustomerKind;
  display_name: string;
  legal_name: string | null;
  trade_name: string | null;
  tax_identifier: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  status: string;
  relationship_status: string;
  customer_role: "buyer" | "owner" | "both";
};
type ConsignmentOwnerLink = { id: string; customer_id: string };
type CustomerVehicleAgreement = {
  id: string;
  owner_id: string;
  vehicle_id: string;
  status: string;
  starts_on: string;
  ends_on: string | null;
};
type Opportunity = OpportunityRecord;
type CrmTask = OpportunityTask;
type Vehicle = {
  id: string;
  registration: string | null;
  stock_number: string | null;
  make: string;
  model: string;
  stock_status: string;
};
type TeamMember = {
  user_id: string;
  display_name: string;
  role_key: string;
  status: string;
};
type Reservation = {
  vehicle_id: string;
  customer_name: string;
  expires_at: string;
  assigned_user_id: string;
};
type ContactAction =
  | "edit"
  | "transfer"
  | "interaction"
  | "convert"
  | "close"
  | "merge"
  | "unmerge"
  | null;

const priorityLabels: Record<string, string> = {
  low: "Baixa",
  normal: "Normal",
  high: "Alta",
  urgent: "Urgente",
};
const stageLabels: Record<string, string> = {
  new: "Novo interesse",
  contacted: "Contactado",
  qualified: "Qualificado",
  proposal: "Proposta",
  reserved: "Reservado",
};
const opportunityStatusLabels: Record<string, string> = {
  active: "Em acompanhamento",
  won: "Convertida",
  lost: "Perdida",
};
const contactStatusLabels: Record<string, string> = {
  unidentified: "Não identificado",
  identified: "Identificado",
  converted: "Convertido",
  closed: "Encerrado",
  merged: "Unido",
};
const channelLabels: Record<string, string> = {
  manual: "Registo manual",
  phone: "Telefone",
  in_person: "Presencial",
  web_chat: "Chat do site",
  whatsapp: "WhatsApp",
  email: "Email",
  form: "Formulário",
  paid_ad: "Anúncio pago",
  vehicle_portal: "Portal automóvel",
  other: "Outro",
};
const directionLabels: Record<string, string> = {
  inbound: "Recebida",
  outbound: "Enviada",
  internal: "Nota interna",
};
const interactionTypeLabels: Record<string, string> = {
  contact_received: "Primeiro contacto",
  message: "Mensagem",
  email: "Email",
  form: "Formulário",
  call: "Chamada",
  meeting: "Reunião/visita",
  note: "Nota",
  status_change: "Alteração",
};
const eventLabels: Record<string, string> = {
  created: "Contacto criado",
  updated: "Contacto atualizado",
  identified: "Contacto identificado",
  interaction_added: "Interação registada",
  converted: "Convertido em oportunidade",
  closed: "Contacto encerrado",
  reopened: "Contacto reaberto",
  transferred: "Responsável alterado",
  merged: "Registo unido",
  merge_reversed: "União desfeita",
};
const normalizedEmail = (value: string | null) =>
  value?.trim().toLocaleLowerCase("pt-PT") || "";
const normalizedPhone = (value: string | null) =>
  value?.replace(/\D/g, "") || "";
const localInput = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString("pt-PT") : "—";
const personLabel = (contact: Contact) =>
  contact.display_name || contact.email || contact.phone || "Contacto sem nome";

export type SaleOpportunitySeed = {
  opportunityId: string;
  customerId: string;
  vehicleId: string;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
};
export function StandCrm({
  organizationId,
  countryCode,
  writable,
  canSupervise,
  initialTab = "contacts",
  onConvertToSale,
}: {
  organizationId: string;
  countryCode: ValidationCountry;
  writable: boolean;
  canSupervise: boolean;
  initialTab?: "contacts" | "opportunities" | "customers";
  onConvertToSale: (seed: SaleOpportunitySeed) => void;
}) {
  const [contacts, setContacts] = useState<Contact[]>([]),
    [contactMerges, setContactMerges] = useState<ContactMerge[]>([]),
    [interactions, setInteractions] = useState<Interaction[]>([]),
    [contactEvents, setContactEvents] = useState<ContactEvent[]>([]),
    [conversations, setConversations] = useState<Conversation[]>([]),
    [conversationMessages, setConversationMessages] = useState<
      ConversationMessage[]
    >([]),
    [customers, setCustomers] = useState<Customer[]>([]),
    [opportunities, setOpportunities] = useState<Opportunity[]>([]),
    [publicVehicleIntakes, setPublicVehicleIntakes] = useState<PublicVehicleIntake[]>([]),
    [publicFinancingRequests, setPublicFinancingRequests] = useState<PublicFinancingRequest[]>([]),
    [tasks, setTasks] = useState<CrmTask[]>([]),
    [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [team, setTeam] = useState<TeamMember[]>([]),
    [reservations, setReservations] = useState<Reservation[]>([]);
  const [consignmentOwners, setConsignmentOwners] = useState<ConsignmentOwnerLink[]>([]);
  const [customerVehicleAgreements, setCustomerVehicleAgreements] = useState<CustomerVehicleAgreement[]>([]);
  const [tab] = useState<"contacts" | "opportunities" | "customers">(
      initialTab,
    ),
    [creating, setCreating] = useState(false),
    [creatingOpportunity, setCreatingOpportunity] = useState(false),
    [creatingCustomer, setCreatingCustomer] = useState(false),
    [newCustomerKind, setNewCustomerKind] = useState<CustomerKind>("individual"),
    [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null),
    [editingCustomer, setEditingCustomer] = useState(false),
    [editCustomerKind, setEditCustomerKind] = useState<CustomerKind>("individual"),
    [selectedContact, setSelectedContact] = useState<Contact | null>(null),
    [contactAction, setContactAction] = useState<ContactAction>(null),
    [selectedOpportunity, setSelectedOpportunity] =
      useState<Opportunity | null>(null),
    [loading, setLoading] = useState(true),
    [message, setMessage] = useState<string | null>(null),
    [messageKind, setMessageKind] = useState<"success" | "error" | "progress">(
      "success",
    ),
    [busyAction, setBusyAction] = useState<string | null>(null),
    [statusFilter, setStatusFilter] = useState("active"),
    [responsibleFilter, setResponsibleFilter] = useState("all"),
    [sourceFilter, setSourceFilter] = useState("all"),
    [vehicleFilter, setVehicleFilter] = useState("all"),
    [fromFilter, setFromFilter] = useState(""),
    [toFilter, setToFilter] = useState(""),
    [search, setSearch] = useState("");

  const notify = (
    text: string,
    kind: "success" | "error" | "progress" = "success",
  ) => {
    setMessage(text);
    setMessageKind(kind);
  };
  const load = async () => {
    setLoading(true);
    const [co, cg, ci, ce, cv, cm, c, o, pvi, pfr, t, v, m, r, col, va] = await Promise.all([
      supabase
        .from("stand_contacts")
        .select(
          "id,branch_id,public_reference,display_name,email,phone,channel,source_label,origin_vehicle_id,assigned_to,status,notes,first_contact_at,last_interaction_at,converted_opportunity_id,closed_reason,merged_into_contact_id,created_at",
        )
        .eq("organization_id", organizationId)
        .order("last_interaction_at", { ascending: false }),
      canSupervise
        ? supabase
            .from("stand_contact_merges")
            .select(
              "id,primary_contact_id,duplicate_contact_id,reason,merged_at,reversed_at,reversal_reason",
            )
            .eq("organization_id", organizationId)
            .order("merged_at", { ascending: false })
        : Promise.resolve({ data: [], error: null }),
      supabase
        .from("stand_contact_interactions")
        .select(
          "id,contact_id,channel,direction,interaction_type,summary,occurred_at,created_by",
        )
        .eq("organization_id", organizationId)
        .order("occurred_at", { ascending: false }),
      supabase
        .from("stand_contact_events")
        .select("id,contact_id,event_type,message,created_at")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false }),
      supabase
        .from("stand_conversations")
        .select("id,contact_id,channel,status,assigned_to,updated_at")
        .eq("organization_id", organizationId)
        .order("updated_at", { ascending: false }),
      supabase
        .from("stand_conversation_messages")
        .select("id,conversation_id,direction,body,created_at")
        .eq("organization_id", organizationId)
        .order("created_at"),
      supabase
        .from("stand_customers")
        .select("id,customer_type,display_name,legal_name,trade_name,tax_identifier,email,phone,address,notes,status,relationship_status,customer_role")
        .eq("organization_id", organizationId)
        .order("display_name"),
      supabase
        .from("stand_opportunities")
        .select(
          "id,customer_id,vehicle_id,public_vehicle_intake_id,public_financing_request_id,origin_contact_id,assigned_to,source,stage,status,priority,last_interaction_at,next_action_at,notes,closed_reason,outcome_code,reopen_reason,created_at",
        )
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false }),
      supabase
        .from("stand_public_vehicle_intakes")
        .select("id,public_reference,request_type,registration,vin,make,model,version,first_registration_year,mileage_km,vehicle_condition,desired_value_cents,currency_code,notes,photo_paths,submitted_at")
        .eq("organization_id", organizationId)
        .order("submitted_at", { ascending: false }),
      supabase
        .from("stand_public_financing_requests")
        .select("id,public_reference,vehicle_description,vehicle_price_cents,down_payment_cents,financed_amount_cents,term_months,estimated_installment_cents,nominal_annual_rate,annual_percentage_rate,submitted_at")
        .eq("organization_id", organizationId)
        .order("submitted_at", { ascending: false }),
      supabase
        .from("stand_crm_tasks")
        .select(
          "id,opportunity_id,contact_id,conversation_id,customer_id,vehicle_id,title,description,due_at,priority_starts_at,priority,status,assigned_to,completion_note,withdrawal_reason,review_status,review_note",
        )
        .eq("organization_id", organizationId)
        .order("due_at"),
      supabase
        .from("vehicles")
        .select("id,registration,stock_number,make,model,stock_status")
        .eq("organization_id", organizationId)
        .eq("creation_status", "active")
        .order("make"),
      supabase.rpc("list_organization_team", {
        p_organization_id: organizationId,
      }),
      supabase
        .from("vehicle_reservations")
        .select("vehicle_id,customer_name,expires_at,assigned_user_id")
        .eq("organization_id", organizationId)
        .eq("status", "active"),
      supabase
        .from("consignment_owners")
        .select("id,customer_id")
        .eq("organization_id", organizationId),
      supabase
        .from("vehicle_consignment_agreements")
        .select("id,owner_id,vehicle_id,status,starts_on,ends_on")
        .eq("organization_id", organizationId),
    ]);
    const error =
      co.error ||
      cg.error ||
      ci.error ||
      ce.error ||
      cv.error ||
      cm.error ||
      c.error ||
      o.error ||
      pvi.error ||
      pfr.error ||
      t.error ||
      v.error ||
      m.error ||
      r.error ||
      col.error ||
      va.error;
    if (error) notify(error.message, "error");
    setContacts((co.data ?? []) as Contact[]);
    setContactMerges((cg.data ?? []) as ContactMerge[]);
    setInteractions((ci.data ?? []) as Interaction[]);
    setContactEvents((ce.data ?? []) as ContactEvent[]);
    setConversations((cv.data ?? []) as Conversation[]);
    setConversationMessages((cm.data ?? []) as ConversationMessage[]);
    setCustomers((c.data ?? []) as Customer[]);
    setOpportunities((o.data ?? []) as Opportunity[]);
    setPublicVehicleIntakes((pvi.data ?? []) as PublicVehicleIntake[]);
    setPublicFinancingRequests((pfr.data ?? []) as PublicFinancingRequest[]);
    setTasks((t.data ?? []) as CrmTask[]);
    setVehicles((v.data ?? []) as Vehicle[]);
    setTeam(
      ((m.data ?? []) as TeamMember[]).filter((x) => x.status === "active"),
    );
    setReservations((r.data ?? []) as Reservation[]);
    setConsignmentOwners((col.data ?? []) as ConsignmentOwnerLink[]);
    setCustomerVehicleAgreements((va.data ?? []) as CustomerVehicleAgreement[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  useEffect(() => {
    if (!message || messageKind === "progress") return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message, messageKind]);
  useEffect(() => {
    if (!selectedCustomer) return;
    const refreshed = customers.find((x) => x.id === selectedCustomer.id);
    if (refreshed && refreshed !== selectedCustomer)
      setSelectedCustomer(refreshed);
  }, [customers, selectedCustomer]);
  useEffect(() => {
    if (!selectedContact) return;
    const refreshed = contacts.find((x) => x.id === selectedContact.id);
    if (refreshed && refreshed !== selectedContact)
      setSelectedContact(refreshed);
  }, [contacts, selectedContact]);
  useEffect(() => {
    if (!selectedOpportunity) return;
    const refreshed = opportunities.find(
      (x) => x.id === selectedOpportunity.id,
    );
    if (refreshed && refreshed !== selectedOpportunity)
      setSelectedOpportunity(refreshed);
  }, [opportunities, selectedOpportunity]);

  const customerMap = useMemo(
      () => Object.fromEntries(customers.map((x) => [x.id, x])),
      [customers],
    ),
    vehicleMap = useMemo(
      () => Object.fromEntries(vehicles.map((x) => [x.id, x])),
      [vehicles],
    ),
    teamMap = useMemo(
      () => Object.fromEntries(team.map((x) => [x.user_id, x.display_name])),
      [team],
    ),
    reservationMap = useMemo(
      () => Object.fromEntries(reservations.map((x) => [x.vehicle_id, x])),
      [reservations],
    );
  const duplicatePairs = useMemo(() => {
    const active = contacts.filter(
      (contact) =>
        contact.status !== "merged" && contact.status !== "converted",
    );
    const pairs: { primary: Contact; duplicate: Contact; match: string }[] = [];
    active.forEach((primary, index) =>
      active.slice(index + 1).forEach((duplicate) => {
        const email = normalizedEmail(primary.email),
          phone = normalizedPhone(primary.phone);
        if (email && email === normalizedEmail(duplicate.email))
          pairs.push({
            primary,
            duplicate,
            match: `Mesmo email: ${primary.email}`,
          });
        else if (phone && phone === normalizedPhone(duplicate.phone))
          pairs.push({
            primary,
            duplicate,
            match: `Mesmo telefone: ${primary.phone}`,
          });
      }),
    );
    return pairs;
  }, [contacts]);
  const inPeriod = (value: string) => {
    const time = new Date(value).getTime();
    return (
      (!fromFilter || time >= new Date(`${fromFilter}T00:00:00`).getTime()) &&
      (!toFilter || time <= new Date(`${toFilter}T23:59:59`).getTime())
    );
  };
  const filteredContacts = useMemo(
    () =>
      contacts.filter((contact) => {
        if (
          statusFilter === "active" &&
          !["unidentified", "identified"].includes(contact.status)
        )
          return false;
        if (
          statusFilter !== "all" &&
          statusFilter !== "active" &&
          contact.status !== statusFilter
        )
          return false;
        if (
          responsibleFilter !== "all" &&
          contact.assigned_to !== responsibleFilter
        )
          return false;
        if (sourceFilter !== "all" && contact.channel !== sourceFilter)
          return false;
        if (
          vehicleFilter !== "all" &&
          contact.origin_vehicle_id !== vehicleFilter
        )
          return false;
        if (!inPeriod(contact.first_contact_at)) return false;
        const term = search.trim().toLocaleLowerCase("pt-PT");
        if (!term) return true;
        const vehicle = contact.origin_vehicle_id
          ? vehicleMap[contact.origin_vehicle_id]
          : null;
        return [
          contact.display_name,
          contact.email,
          contact.phone,
          contact.public_reference,
          contact.source_label,
          vehicle?.registration,
          vehicle?.make,
          vehicle?.model,
        ].some((value) => value?.toLocaleLowerCase("pt-PT").includes(term));
      }),
    [
      contacts,
      statusFilter,
      responsibleFilter,
      sourceFilter,
      vehicleFilter,
      fromFilter,
      toFilter,
      search,
      vehicleMap,
    ],
  );
  const filteredOpportunities = useMemo(
    () =>
      opportunities.filter((opportunity) => {
        if (statusFilter === "active" && opportunity.status !== "active")
          return false;
        if (
          statusFilter !== "all" &&
          statusFilter !== "active" &&
          opportunity.status !== statusFilter
        )
          return false;
        if (
          responsibleFilter !== "all" &&
          opportunity.assigned_to !== responsibleFilter
        )
          return false;
        if (sourceFilter !== "all" && opportunity.source !== sourceFilter)
          return false;
        if (vehicleFilter !== "all" && opportunity.vehicle_id !== vehicleFilter)
          return false;
        if (!inPeriod(opportunity.created_at)) return false;
        const customer = customerMap[opportunity.customer_id],
          vehicle = opportunity.vehicle_id ? vehicleMap[opportunity.vehicle_id] : undefined,
          term = search.trim().toLocaleLowerCase("pt-PT");
        return (
          !term ||
          [
            customer?.display_name,
            customer?.email,
            customer?.phone,
            vehicle?.registration,
            vehicle?.make,
            vehicle?.model,
          ].some((value) => value?.toLocaleLowerCase("pt-PT").includes(term))
        );
      }),
    [
      opportunities,
      statusFilter,
      responsibleFilter,
      sourceFilter,
      vehicleFilter,
      fromFilter,
      toFilter,
      search,
      customerMap,
      vehicleMap,
    ],
  );
  const activeCustomers = customers.filter(
    (x) =>
      (!search.trim() ||
        [x.display_name, x.legal_name, x.trade_name, x.tax_identifier, x.email, x.phone].some((value) =>
          value
            ?.toLocaleLowerCase("pt-PT")
            .includes(search.trim().toLocaleLowerCase("pt-PT")),
        )),
  );

  const createContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusyAction("create-contact");
    notify("A guardar o contacto…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("create_stand_contact", {
      p_organization_id: organizationId,
      p_display_name: String(f.get("display_name") || "") || null,
      p_email: String(f.get("email") || "") || null,
      p_phone: String(f.get("phone") || "") || null,
      p_channel: String(f.get("channel")),
      p_source_label: String(f.get("source_label") || "") || null,
      p_origin_vehicle_id: String(f.get("origin_vehicle_id") || "") || null,
      p_assigned_to: String(f.get("assigned_to")),
      p_notes: String(f.get("notes") || "") || null,
      p_first_interaction: String(f.get("first_interaction")),
      p_first_contact_at: new Date(
        String(f.get("first_contact_at")),
      ).toISOString(),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setCreating(false);
    notify("Contacto guardado.");
    await load();
  };

  const createDirectOpportunity = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const email = String(f.get("email") || "").trim();
    const phone = String(f.get("phone") || "").trim();
    if (!email && !phone) {
      notify("Indique o telefone ou o email do interessado.", "error");
      return;
    }
    setBusyAction("create-opportunity");
    notify("A criar o contacto, a oportunidade e a primeira tarefa…", "progress");
    const channel = String(f.get("channel"));
    const { error } = await supabase.rpc("create_direct_stand_opportunity", {
      p_organization_id: organizationId,
      p_display_name: String(f.get("display_name") || "") || null,
      p_email: email || null,
      p_phone: phone || null,
      p_channel: channel,
      p_source_label: String(f.get("source_label") || "") || null,
      p_vehicle_id: String(f.get("vehicle_id")),
      p_assigned_to: String(f.get("assigned_to")),
      p_priority: String(f.get("priority")),
      p_first_interaction: `Oportunidade registada através de ${channelLabels[channel] || "origem identificada"}.`,
      p_task_title: String(f.get("task_title")),
      p_task_due_at: new Date(String(f.get("task_due_at"))).toISOString(),
      p_notes: String(f.get("notes") || "") || null,
      p_first_contact_at: new Date(String(f.get("first_contact_at"))).toISOString(),
    });
    setBusyAction(null);
    if (error) {
      notify(`Não foi possível criar a oportunidade: ${error.message}`, "error");
      return;
    }
    setCreatingOpportunity(false);
    notify("Oportunidade criada com o contacto e a primeira tarefa associados.");
    await load();
    window.dispatchEvent(new CustomEvent("vorno:tasks-changed"));
  };

  const createCustomer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusyAction("create-customer");
    notify("A guardar o cliente…", "progress");
    const f = new FormData(event.currentTarget);
    const kind = String(f.get("customer_type")) as CustomerKind;
    const identity = {
      kind,
      country: countryCode,
      displayName: String(f.get("display_name") || ""),
      legalName: String(f.get("legal_name") || ""),
      tradeName: String(f.get("trade_name") || ""),
      taxIdentifier: String(f.get("tax_identifier") || ""),
      email: String(f.get("email") || ""),
    };
    const validationErrors = validateCustomerIdentity(identity);
    if (validationErrors.length) {
      setBusyAction(null);
      notify(validationErrors[0], "error");
      return;
    }
    const { error } = await supabase.from("stand_customers").insert({
      organization_id: organizationId,
      customer_type: kind,
      customer_role: String(f.get("customer_role")),
      display_name: String(f.get("display_name") || "").trim(),
      legal_name: kind === "company" ? String(f.get("legal_name") || "").trim() : null,
      trade_name: kind === "company" ? String(f.get("trade_name") || "").trim() : null,
      tax_identifier: String(f.get("tax_identifier") || "").replace(/\D/g, "") || null,
      email: String(f.get("email") || "") ? normalizeEmail(String(f.get("email"))) : null,
      phone: String(f.get("phone") || "") || null,
      address: String(f.get("address") || "") || null,
      notes: String(f.get("notes") || "") || null,
      relationship_status: "prospect",
      created_by: (await supabase.auth.getUser()).data.user?.id,
    });
    setBusyAction(null);
    if (error) {
      notify(`Não foi possível guardar o cliente: ${error.message}`, "error");
      return;
    }
    setCreatingCustomer(false);
    notify("Cliente guardado.");
    await load();
  };
  const updateCustomer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCustomer) return;
    setBusyAction("update-customer");
    notify("A guardar as informações do cliente…", "progress");
    const f = new FormData(event.currentTarget);
    const kind = String(f.get("customer_type")) as CustomerKind;
    const identity = {
      kind,
      country: countryCode,
      displayName: String(f.get("display_name") || ""),
      legalName: String(f.get("legal_name") || ""),
      tradeName: String(f.get("trade_name") || ""),
      taxIdentifier: String(f.get("tax_identifier") || ""),
      email: String(f.get("email") || ""),
    };
    const validationErrors = validateCustomerIdentity(identity);
    if (validationErrors.length) {
      setBusyAction(null);
      notify(validationErrors[0], "error");
      return;
    }
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const { data, error } = await supabase
      .from("stand_customers")
      .update({
        customer_type: kind,
        customer_role: String(f.get("customer_role")),
        display_name: String(f.get("display_name") || "").trim(),
        legal_name: kind === "company" ? String(f.get("legal_name") || "").trim() : null,
        trade_name: kind === "company" ? String(f.get("trade_name") || "").trim() : null,
        tax_identifier: String(f.get("tax_identifier") || "").replace(/\D/g, "") || null,
        email: String(f.get("email") || "") ? normalizeEmail(String(f.get("email"))) : null,
        phone: String(f.get("phone") || "").trim() || null,
        address: String(f.get("address") || "").trim() || null,
        notes: String(f.get("notes") || "").trim() || null,
        status: String(f.get("status")),
        updated_by: userId,
      })
      .eq("organization_id", organizationId)
      .eq("id", selectedCustomer.id)
      .select("id")
      .maybeSingle();
    setBusyAction(null);
    if (error) {
      notify(`Não foi possível guardar o cliente: ${error.message}`, "error");
      return;
    }
    if (!data) {
      notify("Não foi possível alterar este cliente. Verifique as suas permissões.", "error");
      return;
    }
    setEditingCustomer(false);
    notify("Informações do cliente guardadas.");
    await load();
  };
  const updateContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    setBusyAction("edit-contact");
    notify("A guardar as alterações…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("update_stand_contact", {
      p_contact_id: selectedContact.id,
      p_display_name: String(f.get("display_name") || "") || null,
      p_email: String(f.get("email") || "") || null,
      p_phone: String(f.get("phone") || "") || null,
      p_source_label: String(f.get("source_label") || "") || null,
      p_origin_vehicle_id: String(f.get("origin_vehicle_id") || "") || null,
      p_assigned_to: selectedContact.assigned_to,
      p_notes: String(f.get("notes") || "") || null,
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify("Contacto atualizado.");
    await load();
  };
  const transferContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    setBusyAction("transfer-contact");
    notify("A transferir o contacto e o trabalho associado…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("transfer_stand_contact", {
      p_contact_id: selectedContact.id,
      p_to_user_id: String(f.get("assigned_to")),
      p_reason: String(f.get("reason")),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify(
      "Contacto, conversas, tarefas e oportunidade transferidos com histórico preservado.",
    );
    await load();
  };
  const addInteraction = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    setBusyAction("interaction");
    notify("A guardar a interação…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("add_stand_contact_interaction", {
      p_contact_id: selectedContact.id,
      p_channel: String(f.get("channel")),
      p_direction: String(f.get("direction")),
      p_interaction_type: String(f.get("interaction_type")),
      p_summary: String(f.get("summary")),
      p_occurred_at: new Date(String(f.get("occurred_at"))).toISOString(),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify("Interação guardada no histórico.");
    await load();
  };
  const convertContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    setBusyAction("convert");
    notify("A criar a oportunidade e a próxima tarefa…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc(
      "convert_stand_contact_to_opportunity",
      {
        p_contact_id: selectedContact.id,
        p_vehicle_id: String(f.get("vehicle_id")),
        p_priority: String(f.get("priority")),
        p_task_title: String(f.get("task_title")),
        p_task_due_at: new Date(String(f.get("task_due_at"))).toISOString(),
        p_notes: String(f.get("notes") || "") || null,
      },
    );
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify("Contacto convertido em oportunidade com a próxima tarefa criada.");
    await load();
  };
  const closeContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    setBusyAction("close");
    notify("A encerrar o contacto…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("close_stand_contact", {
      p_contact_id: selectedContact.id,
      p_reason: String(f.get("reason")),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify("Contacto encerrado e preservado no histórico.");
    await load();
  };
  const reopenContact = async () => {
    if (!selectedContact) return;
    setBusyAction("reopen");
    notify("A reabrir o contacto…", "progress");
    const { error } = await supabase.rpc("reopen_stand_contact", {
      p_contact_id: selectedContact.id,
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    notify("Contacto reaberto.");
    await load();
  };
  const mergeContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    setBusyAction("merge");
    notify("A unir os registos…", "progress");
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("merge_stand_contacts", {
      p_primary_contact_id: selectedContact.id,
      p_duplicate_contact_id: String(f.get("duplicate_contact_id")),
      p_reason: String(f.get("reason")),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify(
      "Contactos unidos. Todo o histórico foi preservado e a união pode ser desfeita.",
    );
    await load();
  };
  const unmergeContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedContact) return;
    const merge = contactMerges.find(
      (item) =>
        item.duplicate_contact_id === selectedContact.id && !item.reversed_at,
    );
    if (!merge)
      return notify(
        "Não foi encontrada uma união ativa para este contacto.",
        "error",
      );
    setBusyAction("unmerge");
    notify("A desfazer a união…", "progress");
    const { error } = await supabase.rpc("reverse_stand_contact_merge", {
      p_merge_id: merge.id,
      p_reason: String(new FormData(event.currentTarget).get("reason")),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    setContactAction(null);
    notify("União desfeita. Os dois contactos voltaram a ficar independentes.");
    await load();
  };
  const replyConversation = async (
    event: FormEvent<HTMLFormElement>,
    conversationId: string,
  ) => {
    event.preventDefault();
    setBusyAction("conversation-reply");
    const form = event.currentTarget;
    const { error } = await supabase.rpc("reply_to_stand_conversation", {
      p_conversation_id: conversationId,
      p_message: String(new FormData(form).get("message") || ""),
    });
    setBusyAction(null);
    if (error) return notify(error.message, "error");
    form.reset();
    notify("Mensagem enviada.");
    await load();
  };
  const continueConversationAsOpportunity = () => {
    if (!selectedContact) return;
    if (selectedContact.status === "converted") {
      const opportunity = opportunities.find(
        (item) => item.id === selectedContact.converted_opportunity_id,
      );
      if (opportunity) {
        setSelectedContact(null);
        setSelectedOpportunity(opportunity);
      }
      return;
    }
    if (selectedContact.status !== "identified") {
      setContactAction("edit");
      notify(
        "Indique o telefone ou o email do contacto antes de criar a oportunidade.",
        "error",
      );
      return;
    }
    setContactAction("convert");
  };
  if (loading)
    return (
      <section className="stand-crm-empty">
        A preparar a relação comercial…
      </section>
    );

  const concept =
    tab === "contacts"
      ? {
          title: "O que é um contacto?",
          description:
            "É uma pessoa que falou com o stand, mas cuja intenção de comprar ou vender uma viatura ainda não foi confirmada.",
          action:
            "Registe a origem e o primeiro atendimento. Quando existir interesse comercial real, transforme o contacto numa oportunidade.",
        }
      : tab === "opportunities"
        ? {
            title: "O que é uma oportunidade?",
            description:
              "É um contacto com intenção comercial confirmada que está a ser acompanhado por um responsável.",
            action:
              "Aqui ficam a viatura de interesse, o histórico, as tarefas e a próxima ação até resultar numa venda ou numa perda.",
          }
        : {
            title: "O que é um cliente?",
            description:
              "É uma pessoa ou empresa identificada no stand como comprador, proprietário de uma viatura ou ambos.",
            action:
              "Consulte e edite os dados do cliente e acompanhe as viaturas, oportunidades e relações comerciais associadas.",
          };

  return (
    <section className="stand-crm">
      <header className="stand-crm-heading">
        <div>
          <p className="eyebrow">Relação comercial do stand</p>
          <h2>{tab === "customers" ? "Clientes" : tab === "opportunities" ? "Oportunidades" : "Contactos"}</h2>
          <p>{tab === "customers" ? "Consulte e edite as informações pessoais, profissionais e comerciais." : "Registe cada entrada antes de a transformar numa oportunidade."}</p>
        </div>
        {writable && tab === "customers" ? (
          <button className="crm-primary" onClick={() => setCreatingCustomer(true)}>
            + Novo cliente
          </button>
        ) : writable && tab === "contacts" ? (
          <button className="crm-primary" onClick={() => setCreating(true)}>
            + Novo contacto
          </button>
        ) : writable && tab === "opportunities" ? (
          <button className="crm-primary" onClick={() => setCreatingOpportunity(true)}>
            + Nova oportunidade
          </button>
        ) : null}
      </header>
      <section className={`crm-concept crm-concept-${tab}`} aria-label={`Conceito de ${tab}`}>
        <div>
          <strong>{concept.title}</strong>
          <p>{concept.description}</p>
        </div>
        <p>{concept.action}</p>
      </section>
      {message && (
        <button
          className={`crm-notice ${messageKind}`}
          onClick={() => messageKind !== "progress" && setMessage(null)}
          disabled={messageKind === "progress"}
        >
          {message}
          {messageKind !== "progress" && " ×"}
        </button>
      )}
      <div className="crm-summary">
        <article>
          <small>Por identificar</small>
          <strong>
            {contacts.filter((x) => x.status === "unidentified").length}
          </strong>
        </article>
        <article>
          <small>Identificados</small>
          <strong>
            {contacts.filter((x) => x.status === "identified").length}
          </strong>
        </article>
        <article>
          <small>Oportunidades ativas</small>
          <strong>
            {opportunities.filter((x) => x.status === "active").length}
          </strong>
        </article>
        <article>
          <small>Tarefas abertas</small>
          <strong>{tasks.filter((x) => x.status === "open").length}</strong>
        </article>
        {canSupervise && (
          <article className={duplicatePairs.length ? "needs-attention" : ""}>
            <small>Possíveis duplicados</small>
            <strong>{duplicatePairs.length}</strong>
          </article>
        )}
      </div>
      <div className="crm-filters crm-global-filters">
        <label>
          <span>Pesquisar</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nome, contacto ou matrícula"
          />
        </label>
        {tab !== "customers" && (
          <label>
            <span>Estado</span>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="active">Em tratamento</option>
              <option value="all">Todos</option>
              {Object.entries(
                tab === "contacts"
                  ? contactStatusLabels
                  : opportunityStatusLabels,
              ).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
        {canSupervise && tab !== "customers" && (
          <label>
            <span>Comercial</span>
            <select
              value={responsibleFilter}
              onChange={(event) => setResponsibleFilter(event.target.value)}
            >
              <option value="all">Toda a equipa</option>
              {team.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.display_name}
                </option>
              ))}
            </select>
          </label>
        )}
        {tab !== "customers" && (
          <>
            <label>
              <span>Origem</span>
              <select
                value={sourceFilter}
                onChange={(event) => setSourceFilter(event.target.value)}
              >
                <option value="all">Todas</option>
                {Object.entries(channelLabels).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Viatura</span>
              <select
                value={vehicleFilter}
                onChange={(event) => setVehicleFilter(event.target.value)}
              >
                <option value="all">Todas</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration || vehicle.stock_number} ·{" "}
                    {vehicle.make} {vehicle.model}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>De</span>
              <input
                type="date"
                value={fromFilter}
                max={toFilter || undefined}
                onChange={(event) => setFromFilter(event.target.value)}
              />
            </label>
            <label>
              <span>Até</span>
              <input
                type="date"
                value={toFilter}
                min={fromFilter || undefined}
                onChange={(event) => setToFilter(event.target.value)}
              />
            </label>
          </>
        )}
      </div>
      {tab === "contacts" && (
        <>
          {canSupervise && duplicatePairs.length > 0 && (
            <section className="duplicate-alert">
              <div>
                <strong>
                  {duplicatePairs.length === 1
                    ? "Existe 1 possível contacto duplicado"
                    : `Existem ${duplicatePairs.length} possíveis contactos duplicados`}
                </strong>
                <span>
                  Compare os registos antes de os unir. Nada é apagado e
                  qualquer união pode ser desfeita.
                </span>
              </div>
              <button
                onClick={() => {
                  setSelectedContact(duplicatePairs[0].primary);
                  setContactAction("merge");
                }}
              >
                Rever agora
              </button>
            </section>
          )}
          <div className="crm-list">
            {filteredContacts.length === 0 ? (
              <div className="crm-list-empty">
                <strong>Não existem contactos neste filtro.</strong>
                <span>Altere os filtros ou registe um novo contacto.</span>
              </div>
            ) : (
              filteredContacts.map((contact) => {
                const vehicle = contact.origin_vehicle_id
                  ? vehicleMap[contact.origin_vehicle_id]
                  : null;
                return (
                  <button
                    key={contact.id}
                    className="crm-row contact"
                    onClick={() => {
                      setSelectedContact(contact);
                      setContactAction(null);
                    }}
                  >
                    <span>
                      <strong>{personLabel(contact)}</strong>
                      <small>
                        {contact.email ||
                          contact.phone ||
                          "Sem telefone ou email"}
                      </small>
                    </span>
                    <span>
                      <strong>{channelLabels[contact.channel]}</strong>
                      <small>
                        {contact.source_label || "Sem detalhe de origem"}
                      </small>
                    </span>
                    <span>
                      <strong>
                        {vehicle
                          ? `${vehicle.make} ${vehicle.model}`
                          : "Sem viatura de origem"}
                      </strong>
                      <small>
                        {vehicle?.registration ||
                          vehicle?.stock_number ||
                          contact.public_reference}
                      </small>
                    </span>
                    <span>
                      <strong>
                        {teamMap[contact.assigned_to] || "Responsável"}
                      </strong>
                      <small>{formatDate(contact.last_interaction_at)}</small>
                    </span>
                    <span className={`crm-state ${contact.status}`}>
                      {contactStatusLabels[contact.status]}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
      {tab === "opportunities" && (
        <div className="crm-list">
          {filteredOpportunities.length === 0 ? (
            <div className="crm-list-empty">
              <strong>Não existem oportunidades neste filtro.</strong>
              <span>
                Altere os filtros para consultar outro período ou responsável.
              </span>
            </div>
          ) : (
            filteredOpportunities.map((opportunity) => {
              const customer = customerMap[opportunity.customer_id],
                vehicle = opportunity.vehicle_id ? vehicleMap[opportunity.vehicle_id] : undefined,
                reservation = opportunity.vehicle_id ? reservationMap[opportunity.vehicle_id] : undefined;
              return (
                <button
                  key={opportunity.id}
                  className={`crm-row priority-${opportunity.priority}`}
                  onClick={() => setSelectedOpportunity(opportunity)}
                >
                  <span>
                    <strong>{customer?.display_name ?? "Prospecto"}</strong>
                    <small>
                      {channelLabels[opportunity.source] || opportunity.source}{" "}
                      · {priorityLabels[opportunity.priority]}
                    </small>
                  </span>
                  <span>
                    <strong>
                      {vehicle ? `${vehicle.make} ${vehicle.model}` : "Viatura"}
                    </strong>
                    <small>
                      {vehicle?.registration || vehicle?.stock_number}
                    </small>
                  </span>
                  <span>
                    <strong>
                      {teamMap[opportunity.assigned_to] ?? "Comercial"}
                    </strong>
                    <small>
                      {opportunity.next_action_at
                        ? `Próxima tarefa: ${formatDate(opportunity.next_action_at)}`
                        : "Sem próxima tarefa"}
                    </small>
                  </span>
                  <span className={`crm-state ${opportunity.status}`}>
                    {opportunityStatusLabels[opportunity.status]}
                  </span>
                  {reservation && <em>Reserva ativa</em>}
                </button>
              );
            })
          )}
        </div>
      )}
      {tab === "customers" && (
        <div className="crm-list">
          {activeCustomers.length === 0 ? (
            <div className="crm-list-empty">
              <strong>Ainda não existem clientes.</strong>
              <span>Crie o primeiro cliente ou ajuste a pesquisa.</span>
            </div>
          ) : (
            activeCustomers.map((customer) => (
              <button type="button" className="crm-row customer" key={customer.id} onClick={() => setSelectedCustomer(customer)}>
                <span>
                  <strong>{customer.display_name}</strong>
                  <small>
                    {customer.email || customer.phone || "Sem contacto"}
                  </small>
                  <small>
                    {customer.customer_role === "buyer"
                      ? "Comprador"
                      : customer.customer_role === "owner"
                        ? "Proprietário"
                        : "Comprador e proprietário"}
                  </small>
                </span>
                <span>
                  <strong>
                    {
                      opportunities.filter(
                        (opportunity) =>
                          opportunity.customer_id === customer.id,
                      ).length
                    }
                  </strong>
                  <small>oportunidades</small>
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {selectedCustomer && !editingCustomer && (
        <div className="crm-backdrop">
          <section className="crm-dialog" role="dialog" aria-modal="true" aria-labelledby="customer-profile-title">
            <header>
              <div><p className="eyebrow">Perfil do cliente</p><h2 id="customer-profile-title">{selectedCustomer.display_name}</h2><p>{selectedCustomer.customer_role === "buyer" ? "Comprador" : selectedCustomer.customer_role === "owner" ? "Proprietário" : "Comprador e proprietário"}</p></div>
              <button type="button" onClick={() => setSelectedCustomer(null)}>×</button>
            </header>
            <div className="crm-detail-grid">
              <article><small>Email</small><strong>{selectedCustomer.email || "Não indicado"}</strong></article>
              <article><small>Telefone</small><strong>{selectedCustomer.phone || "Não indicado"}</strong></article>
              <article><small>Morada</small><strong>{selectedCustomer.address || "Não indicada"}</strong></article>
              <article><small>Estado</small><strong>{selectedCustomer.status === "active" ? "Ativo" : "Inativo"}</strong></article>
              <article><small>Tipo</small><strong>{selectedCustomer.customer_type === "company" ? "Empresa" : "Particular"}</strong></article>
              <article><small>{selectedCustomer.customer_type === "company" ? "NIPC" : "NIF"}</small><strong>{selectedCustomer.tax_identifier || "Não indicado"}</strong></article>
              {selectedCustomer.customer_type === "company" && <><article><small>Nome legal</small><strong>{selectedCustomer.legal_name || "Não indicado"}</strong></article><article><small>Marca / nome comercial</small><strong>{selectedCustomer.trade_name || "Não indicado"}</strong></article></>}
              <article className="wide"><small>Notas</small><strong>{selectedCustomer.notes || "Sem notas"}</strong></article>
              {(selectedCustomer.customer_role === "owner" || selectedCustomer.customer_role === "both") && (
                <article className="wide customer-owned-vehicles">
                  <small>Viaturas do cliente</small>
                  {(() => {
                    const ownerIds = consignmentOwners.filter((link) => link.customer_id === selectedCustomer.id).map((link) => link.id);
                    const links = customerVehicleAgreements.filter((item) => ownerIds.includes(item.owner_id));
                    return links.length ? links.map((link) => {
                      const linkedVehicle = vehicleMap[link.vehicle_id];
                      return <div className="customer-vehicle-link" key={link.id}><strong>{linkedVehicle ? `${linkedVehicle.make} ${linkedVehicle.model}` : "Viatura associada"}</strong><span>{linkedVehicle?.registration || linkedVehicle?.stock_number || "Sem matrícula"} · {link.status === "active" ? "Consignação ativa" : link.status === "draft" ? "Acordo em rascunho" : "Acordo terminado"}</span></div>;
                    }) : <span>Este cliente ainda não tem viaturas associadas.</span>;
                  })()}
                </article>
              )}
            </div>
            <footer><button type="button" onClick={() => setSelectedCustomer(null)}>Fechar</button>{writable && <button type="button" className="crm-primary" onClick={() => { setEditCustomerKind(selectedCustomer.customer_type); setEditingCustomer(true); }}>Editar</button>}</footer>
          </section>
        </div>
      )}

      {selectedCustomer && editingCustomer && (
        <div className="crm-backdrop">
          <form className="crm-dialog" onSubmit={updateCustomer}>
            <header>
              <div><p className="eyebrow">Editar cliente</p><h2>{selectedCustomer.display_name}</h2><p>Atualize os dados de identificação, contacto e relação com o stand.</p></div>
              <button type="button" onClick={() => setEditingCustomer(false)}>×</button>
            </header>
            <div className="crm-form-grid">
              <label><span>Tipo de cliente *</span><select name="customer_type" required value={editCustomerKind} onChange={(event) => setEditCustomerKind(event.target.value as CustomerKind)}><option value="individual">Particular</option><option value="company">Empresa</option></select></label>
              <label><span>Relação com o stand *</span><select name="customer_role" required defaultValue={selectedCustomer.customer_role}><option value="buyer">Comprador</option><option value="owner">Proprietário</option><option value="both">Comprador e proprietário</option></select></label>
              <label><span>{editCustomerKind === "company" ? "Pessoa de contacto *" : "Nome completo *"}</span><input name="display_name" required minLength={2} defaultValue={selectedCustomer.display_name} autoComplete="name" /></label>
              {editCustomerKind === "company" && <><label><span>Nome legal da empresa *</span><input name="legal_name" required minLength={2} defaultValue={selectedCustomer.legal_name || ""} autoComplete="organization" /></label><label><span>Marca / nome comercial *</span><input name="trade_name" required minLength={2} defaultValue={selectedCustomer.trade_name || ""} autoComplete="organization" /></label></>}
              <label><span>{countryCode === "BR" ? (editCustomerKind === "company" ? "CNPJ" : "CPF") : (editCustomerKind === "company" ? "NIPC" : "NIF")}</span><input name="tax_identifier" inputMode="numeric" defaultValue={formatTaxIdentifier(selectedCustomer.tax_identifier || "", countryCode, editCustomerKind)} maxLength={countryCode === "BR" ? (editCustomerKind === "company" ? 18 : 14) : 9} onInput={(event) => { event.currentTarget.value = formatTaxIdentifier(event.currentTarget.value, countryCode, editCustomerKind); }} /></label>
              <label><span>Telefone</span><input name="phone" defaultValue={selectedCustomer.phone || ""} autoComplete="tel" /></label>
              <label><span>Email</span><input name="email" type="email" defaultValue={selectedCustomer.email || ""} autoComplete="email" maxLength={254} placeholder="nome@empresa.pt" /></label>
              <label><span>Estado</span><select name="status" defaultValue={selectedCustomer.status}><option value="active">Ativo</option><option value="inactive">Inativo</option></select></label>
              <label className="wide"><span>Morada</span><input name="address" defaultValue={selectedCustomer.address || ""} autoComplete="street-address" /></label>
              <label className="wide"><span>Notas</span><textarea name="notes" rows={4} defaultValue={selectedCustomer.notes || ""} /></label>
            </div>
            <footer><button type="button" onClick={() => setEditingCustomer(false)}>Cancelar</button><button className="crm-primary" disabled={busyAction === "update-customer"}>Guardar</button></footer>
          </form>
        </div>
      )}

      {creatingCustomer && (
        <div className="crm-backdrop">
          <form className="crm-dialog" onSubmit={createCustomer}>
            <header>
              <div>
                <p className="eyebrow">Novo cliente</p>
                <h2>Cadastrar cliente</h2>
                <p>Defina obrigatoriamente a relação deste cliente com o stand.</p>
              </div>
              <button type="button" onClick={() => setCreatingCustomer(false)}>×</button>
            </header>
            <div className="crm-form-grid">
              <label><span>Tipo de cliente *</span><select name="customer_type" required value={newCustomerKind} onChange={(event) => setNewCustomerKind(event.target.value as CustomerKind)}><option value="individual">Particular</option><option value="company">Empresa</option></select></label>
              <label><span>Relação com o stand *</span><select name="customer_role" required defaultValue="buyer"><option value="buyer">Comprador</option><option value="owner">Proprietário</option><option value="both">Comprador e proprietário</option></select></label>
              <label><span>{newCustomerKind === "company" ? "Pessoa de contacto *" : "Nome completo *"}</span><input name="display_name" required minLength={2} autoComplete="name" /></label>
              {newCustomerKind === "company" && <><label><span>Nome legal da empresa *</span><input name="legal_name" required minLength={2} autoComplete="organization" /></label><label><span>Marca / nome comercial *</span><input name="trade_name" required minLength={2} autoComplete="organization" /></label></>}
              <label><span>{countryCode === "BR" ? (newCustomerKind === "company" ? "CNPJ" : "CPF") : (newCustomerKind === "company" ? "NIPC" : "NIF")}</span><input name="tax_identifier" inputMode="numeric" maxLength={countryCode === "BR" ? (newCustomerKind === "company" ? 18 : 14) : 9} onInput={(event) => { event.currentTarget.value = formatTaxIdentifier(event.currentTarget.value, countryCode, newCustomerKind); }} /></label>
              <label><span>Telefone</span><input name="phone" autoComplete="tel" /></label>
              <label><span>Email</span><input name="email" type="email" autoComplete="email" maxLength={254} placeholder="nome@empresa.pt" /></label>
              <label className="wide"><span>Morada</span><input name="address" autoComplete="street-address" /></label>
              <label className="wide"><span>Notas</span><textarea name="notes" rows={3} /></label>
            </div>
            <footer>
              <button type="button" onClick={() => setCreatingCustomer(false)}>Cancelar</button>
              <button className="crm-primary" disabled={busyAction === "create-customer"}>Guardar</button>
            </footer>
          </form>
        </div>
      )}

      {creating && (
        <div className="crm-backdrop">
          <form className="crm-dialog" onSubmit={createContact}>
            <header>
              <div>
                <p className="eyebrow">Novo contacto</p>
                <h2>Registar entrada</h2>
                <p>O telefone ou email pode ser acrescentado posteriormente.</p>
              </div>
              <button type="button" onClick={() => setCreating(false)}>
                ×
              </button>
            </header>
            <div className="crm-form-grid">
              <label>
                <span>Nome</span>
                <input name="display_name" autoComplete="name" />
              </label>
              <label>
                <span>Data e hora *</span>
                <input
                  name="first_contact_at"
                  type="datetime-local"
                  required
                  defaultValue={localInput()}
                />
              </label>
              <label>
                <span>Telefone</span>
                <input name="phone" autoComplete="tel" />
              </label>
              <label>
                <span>Email</span>
                <input name="email" type="email" autoComplete="email" />
              </label>
              <label>
                <span>Canal *</span>
                <select name="channel" required defaultValue="phone">
                  {Object.entries(channelLabels).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Detalhe da origem</span>
                <input
                  name="source_label"
                  placeholder="Ex.: anúncio Facebook de agosto"
                />
              </label>
              <label className="wide">
                <span>Viatura de origem</span>
                <select name="origin_vehicle_id" defaultValue="">
                  <option value="">Sem viatura identificada</option>
                  {vehicles.map((vehicle) => (
                    <option value={vehicle.id} key={vehicle.id}>
                      {vehicle.registration || vehicle.stock_number} ·{" "}
                      {vehicle.make} {vehicle.model}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                <span>Responsável *</span>
                <select name="assigned_to" required defaultValue="">
                  <option value="">Escolher</option>
                  {team.map((member) => (
                    <option value={member.user_id} key={member.user_id}>
                      {member.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                <span>O que aconteceu neste primeiro contacto? *</span>
                <textarea name="first_interaction" required rows={4} />
              </label>
              <label className="wide">
                <span>Notas internas</span>
                <textarea name="notes" rows={3} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setCreating(false)}>
                Cancelar
              </button>
              <button
                className="crm-primary"
                disabled={busyAction === "create-contact"}
              >
                Guardar
              </button>
            </footer>
          </form>
        </div>
      )}

      {creatingOpportunity && (
        <div className="crm-backdrop">
          <form className="crm-dialog direct-opportunity-dialog" onSubmit={createDirectOpportunity}>
            <header>
              <div>
                <p className="eyebrow">Nova oportunidade</p>
                <h2>Registar interesse comercial</h2>
                <p>Ao guardar, o contacto, a oportunidade e a primeira tarefa ficam ligados automaticamente.</p>
              </div>
              <button type="button" onClick={() => setCreatingOpportunity(false)}>×</button>
            </header>
            <div className="crm-form-grid">
              <label><span>Nome do interessado</span><input name="display_name" autoComplete="name" /></label>
              <label><span>Data e hora do contacto *</span><input name="first_contact_at" type="datetime-local" required defaultValue={localInput()} /></label>
              <label><span>Telefone</span><input name="phone" autoComplete="tel" /></label>
              <label><span>Email</span><input name="email" type="email" autoComplete="email" /></label>
              <label><span>Origem *</span><select name="channel" required defaultValue="in_person">{Object.entries(channelLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
              <label><span>Detalhe da origem</span><input name="source_label" placeholder="Ex.: abordagem presencial na bomba" /></label>
              <label><span>Viatura de interesse *</span><select name="vehicle_id" required defaultValue=""><option value="">Escolher viatura</option>{vehicles.map((vehicle) => <option value={vehicle.id} key={vehicle.id}>{vehicle.registration || vehicle.stock_number} · {vehicle.make} {vehicle.model}</option>)}</select></label>
              <label><span>Responsável *</span><select name="assigned_to" required defaultValue=""><option value="">Escolher</option>{team.map((member) => <option value={member.user_id} key={member.user_id}>{member.display_name}</option>)}</select></label>
              <label className="wide"><span>Primeira tarefa *</span><input name="task_title" required defaultValue="Contactar o interessado" /></label>
              <label><span>Prioridade *</span><select name="priority" required defaultValue="normal">{Object.entries(priorityLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
              <label><span>Data e hora da tarefa *</span><input name="task_due_at" type="datetime-local" required defaultValue={localInput(new Date(Date.now() + 86400000))} /></label>
              <label className="wide"><span>Notas internas</span><textarea name="notes" rows={3} /></label>
            </div>
            <footer><button type="button" onClick={() => setCreatingOpportunity(false)}>Cancelar</button><button className="crm-primary" disabled={busyAction === "create-opportunity"}>Guardar</button></footer>
          </form>
        </div>
      )}

      {selectedContact && (
        <div className="crm-backdrop">
          <section className="crm-dialog contact-dialog">
            <header>
              <div>
                <p className="eyebrow">{selectedContact.public_reference}</p>
                <h2>{personLabel(selectedContact)}</h2>
                <p>
                  {channelLabels[selectedContact.channel]} ·{" "}
                  {contactStatusLabels[selectedContact.status]}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedContact(null);
                  setContactAction(null);
                }}
              >
                ×
              </button>
            </header>
            <div className="crm-detail-grid">
              <article>
                <small>Telefone</small>
                <strong>{selectedContact.phone || "Não indicado"}</strong>
              </article>
              <article>
                <small>Email</small>
                <strong>{selectedContact.email || "Não indicado"}</strong>
              </article>
              <article>
                <small>Responsável</small>
                <strong>{teamMap[selectedContact.assigned_to] || "—"}</strong>
              </article>
              <article>
                <small>Última interação</small>
                <strong>
                  {formatDate(selectedContact.last_interaction_at)}
                </strong>
              </article>
              <article>
                <small>Origem</small>
                <strong>
                  {selectedContact.source_label ||
                    channelLabels[selectedContact.channel]}
                </strong>
              </article>
              <article>
                <small>Viatura de origem</small>
                <strong>
                  {selectedContact.origin_vehicle_id
                    ? `${vehicleMap[selectedContact.origin_vehicle_id]?.make || ""} ${vehicleMap[selectedContact.origin_vehicle_id]?.model || ""} · ${vehicleMap[selectedContact.origin_vehicle_id]?.registration || vehicleMap[selectedContact.origin_vehicle_id]?.stock_number || "—"}`
                    : "Não identificada"}
                </strong>
              </article>
              {selectedContact.notes && (
                <article className="wide">
                  <small>Notas internas</small>
                  <span>{selectedContact.notes}</span>
                </article>
              )}
              {selectedContact.closed_reason && (
                <article className="wide closed-reason">
                  <small>Motivo do encerramento</small>
                  <strong>{selectedContact.closed_reason}</strong>
                </article>
              )}
              {contactMerges.filter(
                (item) =>
                  item.primary_contact_id === selectedContact.id &&
                  !item.reversed_at,
              ).length > 0 && (
                <article className="wide merged-history">
                  <small>Histórico reunido</small>
                  <strong>
                    {
                      contactMerges.filter(
                        (item) =>
                          item.primary_contact_id === selectedContact.id &&
                          !item.reversed_at,
                      ).length
                    }{" "}
                    registo(s) duplicado(s) ligado(s) a este contacto
                  </strong>
                </article>
              )}
            </div>
            {writable && (
              <div className="contact-actions">
                {canSupervise && selectedContact.status !== "merged" && (
                  <button onClick={() => setContactAction("transfer")}>
                    Transferir para outro comercial
                  </button>
                )}
                {!["converted", "closed", "merged"].includes(
                  selectedContact.status,
                ) && (
                  <>
                    <button onClick={() => setContactAction("edit")}>
                      Editar contacto
                    </button>
                    <button onClick={() => setContactAction("interaction")}>
                      Registar interação
                    </button>
                    <button
                      className="crm-primary"
                      disabled={selectedContact.status !== "identified"}
                      title={
                        selectedContact.status !== "identified"
                          ? "Registe telefone ou email antes de converter."
                          : ""
                      }
                      onClick={() => setContactAction("convert")}
                    >
                      Criar oportunidade
                    </button>
                    {canSupervise &&
                      contacts.some(
                        (item) =>
                          item.id !== selectedContact.id &&
                          !["converted", "merged"].includes(item.status),
                      ) && (
                        <button onClick={() => setContactAction("merge")}>
                          Unir duplicado
                        </button>
                      )}
                    <button
                      className="danger"
                      onClick={() => setContactAction("close")}
                    >
                      Encerrar contacto
                    </button>
                  </>
                )}
                {selectedContact.status === "closed" && canSupervise && (
                  <>
                    <button
                      className="crm-primary"
                      disabled={busyAction === "reopen"}
                      onClick={() => void reopenContact()}
                    >
                      Reabrir contacto
                    </button>
                    <button onClick={() => setContactAction("merge")}>
                      Unir duplicado
                    </button>
                  </>
                )}
                {selectedContact.status === "merged" && canSupervise && (
                  <button onClick={() => setContactAction("unmerge")}>
                    Desfazer união
                  </button>
                )}
                {selectedContact.status === "converted" && (
                  <button
                    className="crm-primary"
                    onClick={() => {
                      const opportunity = opportunities.find(
                        (item) =>
                          item.id === selectedContact.converted_opportunity_id,
                      );
                      if (opportunity) {
                        setSelectedContact(null);
                        setSelectedOpportunity(opportunity);
                      }
                    }}
                  >
                    Abrir oportunidade
                  </button>
                )}
              </div>
            )}
            {contactAction === "transfer" && (
              <form className="crm-subform" onSubmit={transferContact}>
                <h3>Transferir contacto</h3>
                <div className="crm-form-grid">
                  <label className="wide">
                    <span>Novo responsável *</span>
                    <select name="assigned_to" required defaultValue="">
                      <option value="">Escolher</option>
                      {team
                        .filter(
                          (member) =>
                            member.user_id !== selectedContact.assigned_to,
                        )
                        .map((member) => (
                          <option value={member.user_id} key={member.user_id}>
                            {member.display_name}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="wide">
                    <span>Motivo da transferência *</span>
                    <textarea name="reason" required minLength={3} rows={3} />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="crm-primary"
                    disabled={busyAction === "transfer-contact"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {contactAction === "edit" && (
              <form className="crm-subform" onSubmit={updateContact}>
                <h3>Editar contacto</h3>
                <div className="crm-form-grid">
                  <label>
                    <span>Nome</span>
                    <input
                      name="display_name"
                      defaultValue={selectedContact.display_name || ""}
                    />
                  </label>
                  <label>
                    <span>Telefone</span>
                    <input
                      name="phone"
                      defaultValue={selectedContact.phone || ""}
                    />
                  </label>
                  <label>
                    <span>Email</span>
                    <input
                      name="email"
                      type="email"
                      defaultValue={selectedContact.email || ""}
                    />
                  </label>
                  <label>
                    <span>Detalhe da origem</span>
                    <input
                      name="source_label"
                      defaultValue={selectedContact.source_label || ""}
                    />
                  </label>
                  <label className="wide">
                    <span>Viatura de origem</span>
                    <select
                      name="origin_vehicle_id"
                      defaultValue={selectedContact.origin_vehicle_id || ""}
                    >
                      <option value="">Sem viatura identificada</option>
                      {vehicles.map((vehicle) => (
                        <option value={vehicle.id} key={vehicle.id}>
                          {vehicle.registration || vehicle.stock_number} ·{" "}
                          {vehicle.make} {vehicle.model}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="wide">
                    <span>Notas internas</span>
                    <textarea
                      name="notes"
                      rows={3}
                      defaultValue={selectedContact.notes || ""}
                    />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="crm-primary"
                    disabled={busyAction === "edit-contact"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {contactAction === "interaction" && (
              <form className="crm-subform" onSubmit={addInteraction}>
                <h3>Registar interação</h3>
                <div className="crm-form-grid">
                  <label>
                    <span>Data e hora *</span>
                    <input
                      name="occurred_at"
                      type="datetime-local"
                      required
                      defaultValue={localInput()}
                    />
                  </label>
                  <label>
                    <span>Canal *</span>
                    <select
                      name="channel"
                      defaultValue={selectedContact.channel}
                    >
                      {Object.entries(channelLabels).map(([value, label]) => (
                        <option value={value} key={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Sentido *</span>
                    <select name="direction" defaultValue="outbound">
                      {Object.entries(directionLabels).map(([value, label]) => (
                        <option value={value} key={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Tipo *</span>
                    <select name="interaction_type" defaultValue="call">
                      {Object.entries(interactionTypeLabels)
                        .filter(([value]) => value !== "contact_received")
                        .map(([value, label]) => (
                          <option value={value} key={value}>
                            {label}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="wide">
                    <span>Resultado da interação *</span>
                    <textarea name="summary" required rows={4} />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="crm-primary"
                    disabled={busyAction === "interaction"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {contactAction === "convert" && (
              <form
                className="crm-subform opportunity-confirmation"
                onSubmit={convertContact}
              >
                <h3>Criar oportunidade e próxima tarefa</h3>
                <p className="subform-help">
                  Confirme os dados recolhidos na conversa. A oportunidade só
                  nasce acompanhada de uma ação concreta.
                </p>
                <div className="conversation-confirmation">
                  <article>
                    <small>Contacto</small>
                    <strong>{personLabel(selectedContact)}</strong>
                    <span>
                      {selectedContact.phone || selectedContact.email}
                    </span>
                  </article>
                  <article>
                    <small>Origem</small>
                    <strong>
                      {selectedContact.source_label ||
                        channelLabels[selectedContact.channel]}
                    </strong>
                    <span>
                      {selectedContact.origin_vehicle_id
                        ? `${vehicleMap[selectedContact.origin_vehicle_id]?.make || ""} ${vehicleMap[selectedContact.origin_vehicle_id]?.model || ""}`
                        : "Sem viatura identificada"}
                    </span>
                  </article>
                </div>
                <div className="crm-form-grid">
                  <label className="wide">
                    <span>Viatura de interesse *</span>
                    <select
                      name="vehicle_id"
                      required
                      defaultValue={selectedContact.origin_vehicle_id || ""}
                    >
                      <option value="">Escolher viatura</option>
                      {vehicles.map((vehicle) => (
                        <option value={vehicle.id} key={vehicle.id}>
                          {vehicle.registration || vehicle.stock_number} ·{" "}
                          {vehicle.make} {vehicle.model}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Prioridade</span>
                    <select name="priority" defaultValue="normal">
                      {Object.entries(priorityLabels).map(([value, label]) => (
                        <option value={value} key={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Data da tarefa *</span>
                    <input
                      name="task_due_at"
                      type="datetime-local"
                      required
                      defaultValue={localInput(new Date(Date.now() + 86400000))}
                    />
                  </label>
                  <label className="wide">
                    <span>Próxima tarefa *</span>
                    <input
                      name="task_title"
                      required
                      placeholder="Ex.: telefonar para confirmar a visita"
                    />
                  </label>
                  <label className="wide">
                    <span>Notas da oportunidade</span>
                    <textarea
                      name="notes"
                      rows={3}
                      placeholder="Registe apenas os factos confirmados durante a conversa."
                    />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="crm-primary"
                    disabled={busyAction === "convert"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {contactAction === "close" && (
              <form className="crm-subform" onSubmit={closeContact}>
                <h3>Encerrar contacto</h3>
                <p className="subform-help">
                  O contacto e o histórico permanecem guardados e poderão ser
                  reabertos por um responsável autorizado.
                </p>
                <div className="crm-form-grid">
                  <label className="wide">
                    <span>Motivo *</span>
                    <textarea name="reason" required rows={4} />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="danger solid"
                    disabled={busyAction === "close"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {contactAction === "merge" && (
              <form className="crm-subform" onSubmit={mergeContact}>
                <h3>Unir contacto duplicado</h3>
                <p className="subform-help">
                  O contacto aberto será o registo principal. O outro não é
                  apagado: o histórico fica reunido e a união poderá ser
                  desfeita.
                </p>
                <div className="crm-form-grid">
                  <label className="wide">
                    <span>Registo duplicado *</span>
                    <select
                      name="duplicate_contact_id"
                      required
                      defaultValue=""
                    >
                      <option value="">Escolher contacto</option>
                      {contacts
                        .filter(
                          (item) =>
                            item.id !== selectedContact.id &&
                            !["converted", "merged"].includes(item.status),
                        )
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {personLabel(item)} ·{" "}
                            {item.email || item.phone || item.public_reference}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="wide">
                    <span>Motivo da união *</span>
                    <textarea
                      name="reason"
                      required
                      minLength={3}
                      rows={3}
                      placeholder="Ex.: mesmo email confirmado pelo cliente"
                    />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="crm-primary"
                    disabled={busyAction === "merge"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {contactAction === "unmerge" && (
              <form className="crm-subform" onSubmit={unmergeContact}>
                <h3>Desfazer união</h3>
                <p className="subform-help">
                  O registo voltará ao estado anterior, com o respetivo
                  histórico intacto.
                </p>
                <div className="crm-form-grid">
                  <label className="wide">
                    <span>Motivo *</span>
                    <textarea name="reason" required rows={3} />
                  </label>
                </div>
                <footer>
                  <button type="button" onClick={() => setContactAction(null)}>
                    Cancelar
                  </button>
                  <button
                    className="crm-primary"
                    disabled={busyAction === "unmerge"}
                  >
                    Guardar
                  </button>
                </footer>
              </form>
            )}
            {conversations
              .filter((item) => item.contact_id === selectedContact.id)
              .map((conversation) => (
                <section className="crm-conversation" key={conversation.id}>
                  <header>
                    <div>
                      <h3>Conversa do site</h3>
                      <p>
                        {conversation.status === "open"
                          ? "Conversa em acompanhamento"
                          : "Conversa encerrada"}
                      </p>
                    </div>
                    {writable &&
                      !["closed"].includes(selectedContact.status) && (
                        <button
                          className="crm-primary"
                          type="button"
                          onClick={continueConversationAsOpportunity}
                        >
                          {selectedContact.status === "converted"
                            ? "Abrir oportunidade"
                            : selectedContact.status === "identified"
                              ? "Criar oportunidade"
                              : "Completar contacto"}
                        </button>
                      )}
                  </header>
                  <div>
                    {conversationMessages
                      .filter(
                        (message) =>
                          message.conversation_id === conversation.id,
                      )
                      .map((message) => (
                        <article className={message.direction} key={message.id}>
                          <p>{message.body}</p>
                          <time>{formatDate(message.created_at)}</time>
                        </article>
                      ))}
                  </div>
                  {writable && conversation.status === "open" && (
                    <form
                      onSubmit={(event) =>
                        replyConversation(event, conversation.id)
                      }
                    >
                      <label>
                        <span>Responder</span>
                        <textarea name="message" required rows={3} />
                      </label>
                      <button
                        className="crm-primary"
                        disabled={busyAction === "conversation-reply"}
                      >
                        Enviar
                      </button>
                    </form>
                  )}
                </section>
              ))}
            <section className="contact-timeline">
              <h3>Histórico do contacto</h3>
              {(() => {
                const relatedIds = new Set([
                  selectedContact.id,
                  ...contactMerges
                    .filter(
                      (item) =>
                        item.primary_contact_id === selectedContact.id &&
                        !item.reversed_at,
                    )
                    .map((item) => item.duplicate_contact_id),
                ]);
                return [
                  ...interactions
                    .filter((item) => relatedIds.has(item.contact_id))
                    .map((item) => ({
                      id: `i-${item.id}`,
                      at: item.occurred_at,
                      title: `${interactionTypeLabels[item.interaction_type] || item.interaction_type} · ${directionLabels[item.direction] || item.direction}`,
                      text: item.summary,
                    })),
                  ...contactEvents
                    .filter(
                      (item) =>
                        relatedIds.has(item.contact_id) &&
                        item.event_type !== "interaction_added",
                    )
                    .map((item) => ({
                      id: `e-${item.id}`,
                      at: item.created_at,
                      title: eventLabels[item.event_type] || item.event_type,
                      text: item.message || "Alteração registada.",
                    })),
                ]
                  .sort(
                    (a, b) =>
                      new Date(b.at).getTime() - new Date(a.at).getTime(),
                  )
                  .map((item) => (
                    <article key={item.id}>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.text}</p>
                      </div>
                      <time>{formatDate(item.at)}</time>
                    </article>
                  ));
              })()}
            </section>
          </section>
        </div>
      )}

      {selectedOpportunity && (
        <OpportunityDialog
          opportunity={selectedOpportunity}
          publicIntake={publicVehicleIntakes.find((item) => item.id === selectedOpportunity.public_vehicle_intake_id) ?? null}
          financingRequest={publicFinancingRequests.find((item) => item.id === selectedOpportunity.public_financing_request_id) ?? null}
          customerName={
            customerMap[selectedOpportunity.customer_id]?.display_name ??
            "Cliente"
          }
          customerEmail={
            customerMap[selectedOpportunity.customer_id]?.email ?? null
          }
          customerPhone={
            customerMap[selectedOpportunity.customer_id]?.phone ?? null
          }
          vehicles={vehicles}
          team={team}
          tasks={tasks}
          sourceLabel={contacts.find((contact) => contact.id === selectedOpportunity.origin_contact_id)?.source_label ?? null}
          writable={writable}
          canSupervise={canSupervise}
          onConvertToSale={onConvertToSale}
          onClose={() => setSelectedOpportunity(null)}
          onChanged={load}
          notify={notify}
        />
      )}
    </section>
  );
}
